// Share in a record's header: a dropdown with the team link (teammates sign in to see the record) and a public link
// (anyone with it sees a read-only page, no sign-in). Members-only records can't be public.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, errorText } from "../api/client";
import type { ShareState } from "../api/types";
import { useUI } from "../app/context";

const absolute = (path: string) => new URL(path, window.location.origin).href;

export function ShareMenu({ noticeKey }: { noticeKey: string }) {
  const { showToast } = useUI();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const queryKey = ["notice", noticeKey, "share"];
  const state = useQuery({ queryKey, queryFn: () => api<ShareState>(`/api/notices/${encodeURIComponent(noticeKey)}/share`), enabled: open });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !(e.target as HTMLElement).closest(".share-wrap")) setOpen(false);
    };
    document.addEventListener("click", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const copy = async (path: string, said: string) => {
    const url = absolute(path);
    try {
      await navigator.clipboard.writeText(url);
      showToast(said);
    } catch {
      window.prompt("Copy this link", url); // no clipboard (an http page, or permission refused)
    }
    setOpen(false);
  };
  const copyPublic = async () => {
    setBusy(true);
    try {
      const next = state.data?.public ? state.data : await api<ShareState>(`/api/notices/${encodeURIComponent(noticeKey)}/share`, { method: "POST" });
      client.setQueryData(queryKey, next);
      if (next.public) await copy(next.public.url, "Public link copied. Anyone with it can view this record without signing in.");
    } catch (err) {
      showToast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const s = state.data;
  return (
    <span className="share-wrap">
      <button className="btn" id="share" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        Share ▾
      </button>
      {open ? (
        <div className="menu share-menu" role="menu">
          {state.isPending ? (
            <p className="share-note">Loading…</p>
          ) : state.isError || !s ? (
            <p className="share-note">{errorText(state.error)}</p>
          ) : (
            <>
              <button role="menuitem" id="share-team" onClick={() => copy(s.team_url, "Team link copied. Teammates sign in to GovChew to see it.")}>
                <span>
                  <b>Copy team link</b>
                  <small>Private: people sign in to GovChew to see it</small>
                </span>
              </button>
              <button role="menuitem" id="share-public" disabled={!s.public_allowed || busy} onClick={copyPublic}>
                <span>
                  <b>Copy public link</b>
                  <small>{s.public_allowed ? "Anyone with the link can view it, no sign-in" : s.public_blocked}</small>
                </span>
              </button>
            </>
          )}
        </div>
      ) : null}
    </span>
  );
}
